import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static outlets = [
    "credit-indicator",
    "cost-indicator",
    "leads-imports--limit-reached",
    "leads-imports--tabs",
    "leads-imports--credits-options-modal",
  ]

  connect() {
    const findEmailsCheckbox = document.querySelector('input[name="leads_import[find_emails]"]')
    const verifyLeadsCheckbox = document.querySelector('input[name="leads_import[verify_leads]"]')

    if (findEmailsCheckbox || verifyLeadsCheckbox) {
      this.updateIndicators()

      if (findEmailsCheckbox) {
        findEmailsCheckbox.addEventListener("change", () => this.updateIndicators())
      }

      if (verifyLeadsCheckbox) {
        verifyLeadsCheckbox.addEventListener("change", () => this.updateIndicators())
      }
    }
  }

  updateIndicators() {
    const isSingleBucket = this.element.dataset.singleCreditsBucket === "true"
    const isDualBucket = !isSingleBucket

    if (isDualBucket) {
      this.updateLimitReachedIndicator()
    } else {
      this.updateCreditIndicator()
    }
  }

  updateLimitReachedIndicator() {
    let searchCreditsNeeded = 0
    let verifyCreditsNeeded = 0
    let searchLines = 0
    let verifyLines = 0

    // Find emails option
    const findEmailsCheckbox = document.querySelector('input[name="leads_import[find_emails]"]')
    if (findEmailsCheckbox && findEmailsCheckbox.checked) {
      const optionElement = findEmailsCheckbox.closest(".option-item")
      if (optionElement && optionElement.dataset.creditCost) {
        searchCreditsNeeded = parseFloat(optionElement.dataset.creditCost) || 0
      }
      // Get the number of searchable lines
      const indicatorWrapper = document.querySelector("[data-search-lines]")
      if (indicatorWrapper) {
        searchLines = parseInt(indicatorWrapper.dataset.searchLines) || 0
      }
    }

    // Verify leads option
    const verifyLeadsCheckbox = document.querySelector('input[name="leads_import[verify_leads]"]')
    if (verifyLeadsCheckbox && verifyLeadsCheckbox.checked) {
      const optionElement = verifyLeadsCheckbox.closest(".option-item")
      if (optionElement && optionElement.dataset.creditCost) {
        verifyCreditsNeeded = parseFloat(optionElement.dataset.creditCost) || 0
      }
      // Get the number of verifiable lines
      const indicatorWrapper = document.querySelector("[data-verify-lines]")
      if (indicatorWrapper) {
        verifyLines = parseInt(indicatorWrapper.dataset.verifyLines) || 0
      }
    }

    const availableSearchCalls = parseFloat(this.element.dataset.availableSearchCalls) || 0
    const availableVerifyCalls = parseFloat(this.element.dataset.availableVerifyCalls) || 0

    const isSingleBucket = this.element.dataset.singleCreditsBucket === "true"

    const limitReachedElement = document.getElementById("leads-import-limit-reached-indicator")

    if (limitReachedElement) {
      // Get the Stimulus controller instance
      const limitReachedController = this.application.getControllerForElementAndIdentifier(
        limitReachedElement,
        "leads-imports--limit-reached",
      )

      if (limitReachedController) {
        limitReachedController.checkLimits(
          searchCreditsNeeded,
          verifyCreditsNeeded,
          availableSearchCalls,
          availableVerifyCalls,
          isSingleBucket,
        )
      }

      // Update popover content for dual bucket
      this.updateDualBucketPopover(
        limitReachedElement,
        searchCreditsNeeded,
        verifyCreditsNeeded,
        searchLines,
        verifyLines,
        availableSearchCalls,
        availableVerifyCalls,
      )
    }

    // Also try using outlets in case they're available
    if (this.hasLeadsImportsLimitReachedOutlet) {
      this.leadsImportsLimitReachedOutlets.forEach((outlet) => {
        outlet.checkLimits(
          searchCreditsNeeded,
          verifyCreditsNeeded,
          availableSearchCalls,
          availableVerifyCalls,
          isSingleBucket,
        )
      })
    }

    if (this.hasLeadsImportsTabsOutlet) {
      const searchLimitExceeded = searchCreditsNeeded > availableSearchCalls
      const verifyLimitExceeded = verifyCreditsNeeded > availableVerifyCalls
      const anyLimitExceeded = searchLimitExceeded || verifyLimitExceeded

      this.leadsImportsTabsOutlets.forEach((outlet) => {
        outlet.creditsUpdated(anyLimitExceeded ? 1 : 0)
      })
    }

    // Update modal with quota values for dual bucket teams
    if (this.hasLeadsImportsCreditsOptionsModalOutlet) {
      const searchMissing = Math.max(0, searchCreditsNeeded - availableSearchCalls)
      const verifyMissing = Math.max(0, verifyCreditsNeeded - availableVerifyCalls)

      this.leadsImportsCreditsOptionsModalOutlets.forEach((outlet) => {
        outlet.updateQuotas(
          searchCreditsNeeded,
          availableSearchCalls,
          searchMissing,
          verifyCreditsNeeded,
          availableVerifyCalls,
          verifyMissing,
        )
      })
    }
  }

  // Helper method to format credits
  _formatCredit(num) {
    return num.toFixed(1).replace(/\.0$/, "")
  }

  // Helper method to generate popover HTML content
  _generatePopoverHTML(config) {
    const { totalEmails, isSingleBucket } = config

    let html = `
      <div class="cost-estimation-v2 cost-estimation--no-border">
        <div class="cost-estimation-v2__body">
          <div class="cost-estimation-v2__header">
            <div class="cost-estimation-v2__heading">
              <h3 class="cost-estimation-v2__title">Email addresses to process (preview)</h3>
              <div class="cost-estimation-v2__count">${totalEmails.toLocaleString()}</div>
            </div>
          </div>`

    if (isSingleBucket) {
      html += this._generateSingleBucketContent(config)
    } else {
      html += this._generateDualBucketContent(config)
    }

    // Add footer if there are insufficient credits
    if (config.showFooter) {
      html += `
        <div class="cost-estimation-v2__footer">
          <div class="cost-estimation-v2__footer-header">
            <span class="fas fa-shield-halved" aria-hidden="true"></span>
            <span class="cost-estimation-v2__footer-header-text">${i18n.t("js.back.leads_imports.credit_footer.header")}</span>
          </div>
          <span class="cost-estimation-v2__footer-description">${i18n.t("js.back.leads_imports.credit_footer.description")}</span>
        </div>`
    }

    html += `
        </div>
      </div>`

    return html
  }

  _generateSingleBucketContent(config) {
    const { totalCredits, availableCredits } = config
    const stillNeeded = Math.max(0, totalCredits - availableCredits)

    let html = `
          <div class="cost-estimation-v2-section">
            <h4 class="cost-estimation-v2-section__title">Credits</h4>
            <dl class="cost-estimation-v2-credits">
              <div class="cost-estimation-v2-credits__item">
                <dt class="cost-estimation-v2-credits__label">Required:</dt>
                <dd class="cost-estimation-v2-credits__value">${this._formatCredit(totalCredits)}</dd>
              </div>
              <div class="cost-estimation-v2-credits__item">
                <dt class="cost-estimation-v2-credits__label">Available:</dt>
                <dd class="cost-estimation-v2-credits__value">${this._formatCredit(availableCredits)}</dd>
              </div>`

    if (stillNeeded > 0) {
      html += `
              <div class="cost-estimation-v2-credits__item">
                <dt class="cost-estimation-v2-credits__label">Still needed:</dt>
                <dd class="cost-estimation-v2-credits__value">
                  ${this._formatCredit(stillNeeded)}
                  <span class="text-hint far fa-lock-alt" aria-hidden="true"></span>
                </dd>
              </div>`
    }

    html += `
            </dl>
          </div>`

    return html
  }

  _generateDualBucketContent(config) {
    const { searchCredits, verifyCredits, searchLines, verifyLines, availableSearch, availableVerify } = config
    let html = '<div class="row">'

    // Search credits section
    if (searchCredits > 0) {
      const searchStillNeeded = Math.max(0, searchCredits - availableSearch)
      html += `
            <div class="col-6">
              <div class="cost-estimation-v2-section">
                <h4 class="cost-estimation-v2-section__title">Search · ${searchLines.toLocaleString()}</h4>
                <dl class="cost-estimation-v2-credits">
                  <div class="cost-estimation-v2-credits__item">
                    <dt class="cost-estimation-v2-credits__label">Required:</dt>
                    <dd class="cost-estimation-v2-credits__value">${this._formatCredit(searchCredits)}</dd>
                  </div>
                  <div class="cost-estimation-v2-credits__item">
                    <dt class="cost-estimation-v2-credits__label">Available:</dt>
                    <dd class="cost-estimation-v2-credits__value">${this._formatCredit(availableSearch)}</dd>
                  </div>`

      if (searchStillNeeded > 0) {
        html += `
                  <div class="cost-estimation-v2-credits__item">
                    <dt class="cost-estimation-v2-credits__label">Still needed:</dt>
                    <dd class="cost-estimation-v2-credits__value">
                      ${this._formatCredit(searchStillNeeded)}
                      <span class="text-hint far fa-lock-alt" aria-hidden="true"></span>
                    </dd>
                  </div>`
      }

      html += `
                </dl>
              </div>
            </div>`
    }

    // Verify credits section
    if (verifyCredits > 0) {
      const verifyStillNeeded = Math.max(0, verifyCredits - availableVerify)
      html += `
            <div class="col-6">
              <div class="cost-estimation-v2-section">
                <h4 class="cost-estimation-v2-section__title">Verify · ${verifyLines.toLocaleString()}</h4>
                <dl class="cost-estimation-v2-credits">
                  <div class="cost-estimation-v2-credits__item">
                    <dt class="cost-estimation-v2-credits__label">Required:</dt>
                    <dd class="cost-estimation-v2-credits__value">${this._formatCredit(verifyCredits)}</dd>
                  </div>
                  <div class="cost-estimation-v2-credits__item">
                    <dt class="cost-estimation-v2-credits__label">Available:</dt>
                    <dd class="cost-estimation-v2-credits__value">${this._formatCredit(availableVerify)}</dd>
                  </div>`

      if (verifyStillNeeded > 0) {
        html += `
                  <div class="cost-estimation-v2-credits__item">
                    <dt class="cost-estimation-v2-credits__label">Still needed:</dt>
                    <dd class="cost-estimation-v2-credits__value">
                      ${this._formatCredit(verifyStillNeeded)}
                      <span class="text-hint far fa-lock-alt" aria-hidden="true"></span>
                    </dd>
                  </div>`
      }

      html += `
                </dl>
              </div>
            </div>`
    }

    html += "</div>"
    return html
  }

  updateDualBucketPopover(
    element,
    searchCredits,
    verifyCredits,
    searchLines,
    verifyLines,
    availableSearch,
    availableVerify,
  ) {
    // Check if Bootstrap popover is initialized
    const popoverInstance = bootstrap.Popover.getInstance(element)
    if (!popoverInstance) return

    const totalEmails = searchLines + verifyLines

    const popoverContent = this._generatePopoverHTML({
      totalEmails,
      isSingleBucket: false,
      searchCredits,
      verifyCredits,
      searchLines,
      verifyLines,
      availableSearch,
      availableVerify,
      showFooter: false, // Dual bucket doesn't show footer
    })

    // Update the popover content
    popoverInstance.setContent({ ".popover-body": popoverContent })
  }

  updateCreditIndicator() {
    let totalCredits = 0
    let totalEmails = 0
    let verifyCredits = 0
    let searchCredits = 0

    const findEmailsCheckbox = document.querySelector('input[name="leads_import[find_emails]"]')
    if (findEmailsCheckbox && findEmailsCheckbox.checked) {
      const optionElement = findEmailsCheckbox.closest(".option-item")
      if (optionElement && optionElement.dataset.creditCost) {
        const cost = parseFloat(optionElement.dataset.creditCost) || 0
        searchCredits = cost
        totalCredits += cost
      }
      // Get the number of searchable lines from data attributes
      const indicatorWrapper = document.querySelector("[data-search-lines]")
      if (indicatorWrapper) {
        const searchLines = parseInt(indicatorWrapper.dataset.searchLines) || 0
        totalEmails += searchLines
      }
    }

    const verifyLeadsCheckbox = document.querySelector('input[name="leads_import[verify_leads]"]')
    if (verifyLeadsCheckbox && verifyLeadsCheckbox.checked) {
      const optionElement = verifyLeadsCheckbox.closest(".option-item")
      if (optionElement && optionElement.dataset.creditCost) {
        const cost = parseFloat(optionElement.dataset.creditCost) || 0
        verifyCredits = cost
        totalCredits += cost
      }
      // Get the number of verifiable lines from data attributes
      const indicatorWrapper = document.querySelector("[data-verify-lines]")
      if (indicatorWrapper) {
        const verifyLines = parseInt(indicatorWrapper.dataset.verifyLines) || 0
        totalEmails += verifyLines
      }
    }

    // Calculate missing credits
    const isSingleBucket = this.element.dataset.singleCreditsBucket === "true"
    let availableCredits = 0

    if (isSingleBucket) {
      // For single bucket, use the minimum of available search and verify calls
      const availableSearchCalls = parseFloat(this.element.dataset.availableSearchCalls) || 0
      const availableVerifyCalls = parseFloat(this.element.dataset.availableVerifyCalls) || 0
      availableCredits = Math.min(availableSearchCalls, availableVerifyCalls)
    } else {
      // For dual bucket, use verify calls (as it was before)
      availableCredits = parseFloat(this.element.dataset.availableVerifyCalls) || 0
    }

    const missingCredits = Math.max(0, totalCredits - availableCredits)

    if (this.creditIndicatorOutlets.length > 0) {
      this.creditIndicatorOutlets.forEach((outlet) => {
        if (this.element.contains(outlet.element)) {
          outlet.updateCount(totalCredits)
          outlet.updateMissingCredits(missingCredits)

          // Update popover content dynamically
          this.updatePopoverContent(
            outlet.element,
            totalCredits,
            totalEmails,
            verifyCredits,
            searchCredits,
            availableCredits,
          )
        }
      })
    }

    if (this.costIndicatorOutlets.length > 0) {
      this.costIndicatorOutlets.forEach((outlet) => {
        outlet.updateCost(totalCredits)
      })
    }

    // Notify tabs controller about credit changes
    if (this.hasLeadsImportsTabsOutlet) {
      this.leadsImportsTabsOutlets.forEach((outlet) => {
        outlet.creditsUpdated(missingCredits)
      })
    }

    // Update modal with credit values
    if (this.hasLeadsImportsCreditsOptionsModalOutlet) {
      this.leadsImportsCreditsOptionsModalOutlets.forEach((outlet) => {
        outlet.updateCredits(totalCredits, availableCredits, missingCredits)
      })
    }
  }

  updatePopoverContent(element, totalCredits, totalEmails, verifyCredits, searchCredits, availableCredits) {
    // Check if Bootstrap popover is initialized
    const popoverInstance = bootstrap.Popover.getInstance(element)
    if (!popoverInstance) return

    const hasInsufficientCredits = totalCredits > availableCredits

    const popoverContent = this._generatePopoverHTML({
      totalEmails,
      isSingleBucket: true,
      totalCredits,
      availableCredits,
      showFooter: hasInsufficientCredits,
    })

    // Update the popover content
    popoverInstance.setContent({ ".popover-body": popoverContent })
  }
}
